import { api, endpoints } from '../api/config';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Dialog, DialogTitle, DialogContent, IconButton, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, Button, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PrintIcon from '@mui/icons-material/Print';
import { getFutureDateInputProps } from '../utils/dateConstraints';
import {
  COLUMN_MIN_SIZE,
  COLUMN_SIZE,
  DIALOG_CLOSE_BUTTON_STYLE,
  DIALOG_MAX_WIDTH
} from '../constants/styles';

// Returns true when a picked value indicates a fully-picked order
const isPickedComplete = (pickedValue) => {
  if (pickedValue === null || pickedValue === undefined || pickedValue === '') return false;
  const str = String(pickedValue).trim();
  if (str === '1' || str.toLowerCase() === 'true' || str.toLowerCase() === 'yes' || str.toLowerCase() === 'complete') return true;
  const match = str.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (match) {
    const picked = parseInt(match[1], 10);
    const total = parseInt(match[2], 10);
    return total > 0 && picked >= total;
  }
  return false;
};

// Format a picked-count value for display (e.g. "3/5" → "3", true → "Complete", etc.)
const formatPickedCount = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const str = String(value).trim();
  if (str === '1' || str.toLowerCase() === 'true') return 'Complete';
  if (str === '0' || str.toLowerCase() === 'false') return 'Not Picked';

  // Extract only the X value from X/Y format
  const match = str.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (match) {
    return match[1]; // Return only the X value
  }

  return str;
};

const DropshipFilterBar = ({ 
  statusFilter, 
  setStatusFilter, 
  shipDateFrom, 
  shipDateTo, 
  trackingNumber, 
  transactionId,
  poNumber,
  onApply,
  onViewDetails
}) => {
  const [shipDateFromDraft, setShipDateFromDraft] = useState(shipDateFrom);
  const [shipDateToDraft, setShipDateToDraft] = useState(shipDateTo);
  const [trackingNumberDraft, setTrackingNumberDraft] = useState(trackingNumber);
  const [transactionIdDraft, setTransactionIdDraft] = useState(transactionId);
  const [poNumberDraft, setPoNumberDraft] = useState(poNumber);

  // Sync draft states with external changes (e.g. from local storage on mount)
  useEffect(() => {
    setShipDateFromDraft(shipDateFrom);
  }, [shipDateFrom]);
  useEffect(() => {
    setShipDateToDraft(shipDateTo);
  }, [shipDateTo]);
  useEffect(() => {
    setTrackingNumberDraft(trackingNumber);
  }, [trackingNumber]);
  useEffect(() => {
    setTransactionIdDraft(transactionId);
  }, [transactionId]);
  useEffect(() => {
    setPoNumberDraft(poNumber);
  }, [poNumber]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="status-filter-label" shrink={!!statusFilter}>Status</InputLabel>
        <Select
          labelId="status-filter-label"
          value={statusFilter}
          label="Status"
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{
            fontSize: '0.8125rem',
            height: '32px',
            '& .MuiSelect-select': { fontSize: '0.8125rem', py: 0.75 }
          }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
        </Select>
      </FormControl>
      <TextField
        label="Order Date From"
        type="date"
        size="small"
        value={shipDateFromDraft}
        onChange={(e) => setShipDateFromDraft(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={getFutureDateInputProps()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonthIcon sx={{ fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 150,
          '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
        }}
      />
      <TextField
        label="Order Date To"
        type="date"
        size="small"
        value={shipDateToDraft}
        onChange={(e) => setShipDateToDraft(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={getFutureDateInputProps()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonthIcon sx={{ fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 150,
          '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
        }}
      />
      <TextField
        label="Tracking Number"
        size="small"
        placeholder="Search tracking..."
        value={trackingNumberDraft}
        onChange={(e) => setTrackingNumberDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onApply(shipDateFromDraft, shipDateToDraft, trackingNumberDraft, transactionIdDraft, poNumberDraft);
          }
        }}
        sx={{
          minWidth: 200,
          '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
        }}
      />
      <TextField
        label="SO ID"
        size="small"
        placeholder="Search SO ID..."
        value={transactionIdDraft}
        onChange={(e) => setTransactionIdDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onApply(shipDateFromDraft, shipDateToDraft, trackingNumberDraft, transactionIdDraft, poNumberDraft);
          }
        }}
        sx={{
          minWidth: 200,
          '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
        }}
      />
      <TextField
        label="PO Number"
        size="small"
        placeholder="Search PO..."
        value={poNumberDraft}
        onChange={(e) => setPoNumberDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onApply(shipDateFromDraft, shipDateToDraft, trackingNumberDraft, transactionIdDraft, poNumberDraft);
          }
        }}
        sx={{
          minWidth: 200,
          '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
        }}
      />
      <Button
        variant="contained"
        onClick={() => onApply(shipDateFromDraft, shipDateToDraft, trackingNumberDraft, transactionIdDraft, poNumberDraft)}
        disabled={
          shipDateFromDraft === shipDateFrom &&
          shipDateToDraft === shipDateTo &&
          trackingNumberDraft === trackingNumber &&
          transactionIdDraft === transactionId &&
          poNumberDraft === poNumber
        }
        sx={{
          fontSize: '0.8125rem',
          height: '32px',
          textTransform: 'none',
          px: 2
        }}
      >
        Apply Filters
      </Button>
      <Button
        variant="contained"
        onClick={onViewDetails}
        disabled={statusFilter === 'all'}
        sx={{
          fontSize: '0.8125rem',
          height: '32px',
          textTransform: 'none',
          px: 2
        }}
      >
        View Details
      </Button>
    </Box>
  );
};

const DropshipSalesOrders = () => {
  // Ship date and tracking number filters and drafts
  const [statusFilter, setStatusFilter] = useState('all');
  const [shipDateFrom, setShipDateFrom] = useState('');
  const [shipDateTo, setShipDateTo] = useState('');
  const [trackingNumber, setTrackingNumber] = useState(() => localStorage.getItem('dropship-tracking-number') || '');
  const [transactionId, setTransactionId] = useState(() => localStorage.getItem('dropship-transaction-id') || '');
  const [poNumber, setPoNumber] = useState(() => localStorage.getItem('dropship-po-number') || '');

  // Second-level (detailed) dialog state
  const [detailedDialogOpen, setDetailedDialogOpen] = useState(false);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedData, setDetailedData] = useState([]);

  // View Details dialog state (all open orders line-level data)
  const [viewDetailsDialogOpen, setViewDetailsDialogOpen] = useState(false);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
  const [viewDetailsError, setViewDetailsError] = useState(null);
  const [viewDetailsData, setViewDetailsData] = useState([]);

  const [selectedLineLevelRows, setSelectedLineLevelRows] = useState(new Set());
  const [selectedDetailedRows, setSelectedDetailedRows] = useState(new Set());
  const [selectedViewDetailsRows, setSelectedViewDetailsRows] = useState(new Set());

  // Line-level dialog state (first-level nested)
  const [lineLevelDialogOpen, setLineLevelDialogOpen] = useState(false);
  const [lineLevelAllowNested, setLineLevelAllowNested] = useState(true);
  const [lineLevelLoading, setLineLevelLoading] = useState(false);
  const [lineLevelError, setLineLevelError] = useState(null);
  const [lineLevelData, setLineLevelData] = useState([]);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);

  // Column visibility states (persisted)
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('dropship-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [lineLevelColumnVisibility, setLineLevelColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('dropship-linelevel-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [detailedColumnVisibility, setDetailedColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('dropship-detailed-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [viewDetailsColumnVisibility, setViewDetailsColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('dropship-viewdetails-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('dropship-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);
  useEffect(() => {
    localStorage.setItem('dropship-linelevel-column-visibility', JSON.stringify(lineLevelColumnVisibility));
  }, [lineLevelColumnVisibility]);
  useEffect(() => {
    localStorage.setItem('dropship-detailed-column-visibility', JSON.stringify(detailedColumnVisibility));
  }, [detailedColumnVisibility]);
  useEffect(() => {
    localStorage.setItem('dropship-viewdetails-column-visibility', JSON.stringify(viewDetailsColumnVisibility));
  }, [viewDetailsColumnVisibility]);

  const getTrackingNumber = useCallback((row) => {
    const value = row?.tracking_number || row?.trackingNumber || row?.pro_number || row?.proNumber || '';
    return String(value).trim();
  }, []);

  const rowHasLabelIdentifier = useCallback(
    (row) => Boolean(getTrackingNumber(row)),
    [getTrackingNumber]
  );

  const getRowSelectionKey = useCallback((row) => {
    const trackingNumber = getTrackingNumber(row);
    const lineId = String(row?.line_id || row?.internal_id || row?.transaction_id || row?.id || '');
    const item = String(row?.item || row?.name || '');
    const fingerprint = JSON.stringify({
      tracking_number: row?.tracking_number ?? row?.trackingNumber ?? row?.pro_number ?? row?.proNumber ?? '',
      line_id: row?.line_id ?? row?.internal_id ?? row?.transaction_id ?? row?.id ?? '',
      item: row?.item ?? row?.name ?? '',
      sscc_codes: row?.sscc_codes ?? row?.sscc_code ?? '',
      location: row?.location ?? row?.location_id ?? '',
      bin_number: row?.bin_number ?? '',
      picked_time: row?.picked_time ?? '',
      picker_name: row?.picker_name ?? '',
    });
    return [trackingNumber, lineId, item, fingerprint].filter(Boolean).join('|');
  }, [getTrackingNumber]);

  const updateSelection = useCallback((setter, rowKey, isSelected) => {
    setter((prev) => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(rowKey);
      } else {
        next.delete(rowKey);
      }
      return next;
    });
  }, []);

  const getSelectableRowKeys = useCallback((rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.reduce((acc, row) => {
      if (getTrackingNumber(row)) {
        acc.push(getRowSelectionKey(row));
      }
      return acc;
    }, []);
  }, [getRowSelectionKey, getTrackingNumber]);

  const toggleSelectAllRows = useCallback((setter, rows, isSelected) => {
    setter(isSelected ? new Set(getSelectableRowKeys(rows)) : new Set());
  }, [getSelectableRowKeys]);

  const getSelectedTrackingNumbers = useCallback((rows, selectedRows) => {
    if (!Array.isArray(rows) || !selectedRows || selectedRows.size === 0) return [];
    const trackingNumbers = rows.reduce((acc, row) => {
      const rowKey = getRowSelectionKey(row);
      if (selectedRows.has(rowKey)) {
        const trackingNumber = getTrackingNumber(row);
        if (trackingNumber) {
          acc.push(trackingNumber);
        }
      }
      return acc;
    }, []);

    return Array.from(new Set(trackingNumbers));
  }, [getRowSelectionKey, getTrackingNumber]);

  const dropshipOrdersQuery = useQuery({
    queryKey: ['dropship-sales-orders', statusFilter, shipDateFrom, shipDateTo, trackingNumber, transactionId, poNumber],
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    queryFn: async () => {
      try {
        const params = {};
        if (shipDateFrom) params.order_date_from = shipDateFrom;
        if (shipDateTo) params.order_date_to = shipDateTo;
        if (trackingNumber) params.tracking_number = trackingNumber;
        if (transactionId) params.transaction_id = transactionId;
        if (poNumber) params.po_number = poNumber;
        if (statusFilter && statusFilter !== 'all') {
          params.status = statusFilter === 'closed' ? 'closed' : 'open';
        }
        const response = await api.get(endpoints.dropshipSalesOrders, { params });
        const respData = response.data?.data ?? response.data ?? [];
        return Array.isArray(respData) ? respData : respData ? [respData] : [];
      } catch (err) {
        console.error('Error fetching dropship sales orders:', err);
        throw new Error(err?.response?.data?.message || err?.message || 'Failed to fetch data');
      }
    }
  });

  const data = dropshipOrdersQuery.data || [];
  const isLoading = dropshipOrdersQuery.isLoading;
  const error = dropshipOrdersQuery.error?.message || null;
  const { refetch } = dropshipOrdersQuery;

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const escapeZpl = (value) => String(value ?? '').replaceAll('^', ' ').replaceAll('~', ' ');

  const normalizePackingSlip = (source) => {
    if (!source) return null;

    if (typeof source === 'string') {
      const trimmed = source.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
      try {
        const decoded = JSON.parse(trimmed);
        return normalizePackingSlip(decoded);
      } catch {
        return null;
      }
    }

    if (Array.isArray(source)) {
      if (source.length === 0) return null;
      return normalizePackingSlip(source[0]);
    }

    if (typeof source === 'object') {
      if (source.data && typeof source.data === 'object' && !Array.isArray(source.data)) {
        return source.data;
      }
      return source;
    }

    return null;
  };

  const looksLikeHtml = (bytes) => {
    const sample = new TextDecoder().decode(bytes.slice(0, 100)).trim().toLowerCase();
    return sample.startsWith('<!doctype') || sample.startsWith('<html') || sample.startsWith('<?xml') || sample.startsWith('<body') || sample.startsWith('<!');
  };

  const detectImageType = (bytes) => {
    if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
    if (bytes.length >= 4 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) return 'image/gif';
    if (bytes.length >= 12 && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return 'image/webp';
    return '';
  };

  const loadImageElement = (bytes, typeHint) => new Promise((resolve, reject) => {
    const blob = new Blob([bytes], { type: typeHint || 'image/png' });
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to decode logo image'));
    };
    img.src = objectUrl;
  });

  const resizeImageOptimized = (image) => {
    const maxWidth = 395;
    const maxHeight = 125;
    const scale = Math.min(maxWidth / (image.width || 1), maxHeight / (image.height || 1), 1);
    const width = Math.max(1, Math.round((image.width || 1) * scale));
    const height = Math.max(1, Math.round((image.height || 1) * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);
    return canvas;
  };

  const enhanceImageForZpl = (canvas) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const px = imageData.data;
    const contrast = 1.55;

    for (let i = 0; i < px.length; i += 4) {
      const alpha = px[i + 3];
      if (alpha < 128) {
        px[i] = 255;
        px[i + 1] = 255;
        px[i + 2] = 255;
        px[i + 3] = 255;
        continue;
      }
      const gray = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      const contrasted = Math.max(0, Math.min(255, (gray - 128) * contrast + 128));
      const bw = contrasted < 155 ? 0 : 255;
      px[i] = bw;
      px[i + 1] = bw;
      px[i + 2] = bw;
      px[i + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
    return imageData;
  };

  const convertToZplBitmap = (imageData, width, height) => {
    const bytesPerRow = Math.ceil(width / 8);
    const hexRows = [];

    for (let y = 0; y < height; y++) {
      let rowHex = '';
      for (let xByte = 0; xByte < bytesPerRow; xByte++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = xByte * 8 + bit;
          if (x >= width) continue;
          const idx = (y * width + x) * 4;
          const isBlack = imageData.data[idx] < 128;
          if (isBlack) byte |= (1 << (7 - bit));
        }
        rowHex += byte.toString(16).padStart(2, '0').toUpperCase();
      }
      hexRows.push(rowHex);
    }

    const hex = hexRows.join('');
    const chunked = [];
    for (let i = 0; i < hex.length; i += 80) {
      chunked.push(hex.slice(i, i + 80));
    }

    const totalBytes = bytesPerRow * height;
    return {
      totalBytes,
      bytesPerRow,
      hexData: chunked.join('\n')
    };
  };

  const convertBytesToZplImage = async (bytes, typeHint) => {
    const image = await loadImageElement(bytes, typeHint);
    const canvas = resizeImageOptimized(image);
    const imageData = enhanceImageForZpl(canvas);
    const { totalBytes, bytesPerRow, hexData } = convertToZplBitmap(imageData, canvas.width, canvas.height);
    return `^FO35,15\n^GFA,${totalBytes},${totalBytes},${bytesPerRow},${hexData}\n^FS`;
  };

  const fetchImageBytes = async (url, headers = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal
      });
      const arrayBuffer = await resp.arrayBuffer().catch(() => new ArrayBuffer(0));
      return {
        ok: resp.ok,
        status: resp.status,
        contentType: resp.headers.get('content-type') || '',
        bytes: new Uint8Array(arrayBuffer)
      };
    } catch (error) {
      return {
        ok: false,
        status: 0,
        contentType: '',
        bytes: new Uint8Array(0),
        error: error?.message || 'Failed to fetch image',
        isNetworkError: true
      };
    } finally {
      clearTimeout(timer);
    }
  };

  const normalizeCustomerImageUrl = (rawUrl) => {
    const value = String(rawUrl || '').trim();
    if (!value) return '';

    if (value.startsWith('//')) {
      return `https:${value}`;
    }

    if (value.startsWith('/core')) {
      return `https://4809897.app.netsuite.com${value}`;
    }

    try {
      const parsed = new URL(value);
      if (parsed.hostname.includes('.restlets.api.netsuite.com')) {
        parsed.hostname = parsed.hostname.replace('.restlets.api.netsuite.com', '.app.netsuite.com');
      }
      return parsed.toString();
    } catch {
      if (!/^https?:\/\//i.test(value)) {
        try {
          return new URL(`https://${value}`).toString();
        } catch {
          return '';
        }
      }
      return value;
    }
  };

  const getProxyUrlCandidates = (targetUrl) => {
    const candidates = [];
    if (!targetUrl || typeof targetUrl !== 'string') return candidates;
    // 1. Direct URL (works in environments where CORS is permitted)
    candidates.push(targetUrl);
    try {
      const parsed = new URL(targetUrl);
      // 2. Local dev Vite proxies (works on localhost:5173)
      if (parsed.pathname.includes('/core/media/media.nl') || parsed.pathname.startsWith('/core/')) {
        candidates.push(`/netsuite-media${parsed.pathname}${parsed.search}`);
      }
      candidates.push(`/customer-image-proxy?url=${encodeURIComponent(targetUrl)}`);
      // 3. Production CORS-safe image proxies (works on https://jyswms.com in production)
      candidates.push(`https://wsrv.nl/?url=${encodeURIComponent(targetUrl)}`);
      candidates.push(`https://images.weserv.nl/?url=${encodeURIComponent(targetUrl)}`);
    } catch {}
    return candidates;
  };

  const buildPackingSlipImageZpl = async (packingSlip) => {
    const rawCustomerUrl = packingSlip?.customer_url || packingSlip?.customerUrl || packingSlip?.customer_image_url || '';
    const customerUrl = normalizeCustomerImageUrl(rawCustomerUrl);
    if (!customerUrl || !/^https?:\/\//i.test(customerUrl)) return '';

    const referer = (() => {
      try {
        return new URL(customerUrl).origin;
      } catch {
        return undefined;
      }
    })();

    const browserHeaders = {
      'Accept': 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8'
    };

    const urlsToTry = getProxyUrlCandidates(customerUrl);
    let imageResp = null;

    for (const url of urlsToTry) {
      try {
        const resp = await fetchImageBytes(url, browserHeaders);
        if (resp.ok && resp.bytes && resp.bytes.length > 0 && !looksLikeHtml(resp.bytes)) {
          imageResp = resp;
          break;
        }
      } catch {}
    }

    if (!imageResp || !imageResp.bytes || imageResp.bytes.length === 0) return '';
    if (looksLikeHtml(imageResp.bytes)) return '';

    const detectedType = detectImageType(imageResp.bytes);
    const contentType = (imageResp.contentType || '').toLowerCase();
    const typeHint = detectedType || (contentType.startsWith('image/') ? contentType : 'image/png');
    if (!typeHint) return '';

    try {
      return await convertBytesToZplImage(imageResp.bytes, typeHint);
    } catch {
      return '';
    }
  };

  const cleanShipViaText = (value) => String(value || '')
    .replaceAll('Ã,Â®', '\u00AE')
    .replaceAll('Ã®', '\u00AE')
    .replaceAll('Â®', '\u00AE')
    .replaceAll('Ã', '')
    .replaceAll('Â', '')
    .replace(/\s+/g, ' ')
    .trim();

  const generatePackingSlipZplFromPackingSlip = (packingSlip, logoZpl = '') => {
    const shippingTo = packingSlip?.shipping_to || {};

    const soNumber = String(packingSlip?.transaction_id || '').trim();
    const shipDateRaw = String(packingSlip?.ship_date || '').trim();
    const formattedShipDate = (() => {
      if (!shipDateRaw) {
        const now = new Date();
        return `${now.getMonth() + 1}/${now.getDate()}/${now.getFullYear()}`;
      }
      const parsed = new Date(shipDateRaw);
      if (!isNaN(parsed.getTime())) return `${parsed.getMonth() + 1}/${parsed.getDate()}/${parsed.getFullYear()}`;
      return shipDateRaw;
    })();

    const poNumber = String(packingSlip?.po_number || '').trim();
    const internalId = String(packingSlip?.internal_id || '').trim();
    const packingSlipId = String(packingSlip?.packing_id || '').trim();

    const shipVia = cleanShipViaText(packingSlip?.ship_via || '');
    const poLineCount = poNumber ? Math.ceil(poNumber.length / 22) : 1;
    const orderLineCount = internalId ? Math.ceil(internalId.length / 22) : 0;
    const finalHeaderBottomY = 85 + (poLineCount * 35) + (orderLineCount * 35) + 8;
    const shipViaStartY = Math.max(140, finalHeaderBottomY + 12);
    const shipViaLines = shipVia ? shipVia.split(' ').filter(Boolean) : [];
    const shipViaZpl = shipViaLines
      .map((word, idx) => `^FO430,${shipViaStartY + (idx * 72)}^FB350,1,0,R^FD${escapeZpl(word)}^FS`)
      .join('\n');

    const companyTo = String(shippingTo?.company || '').trim();
    const addressRaw = String(shippingTo?.address || '').trim();
    const addressParts = addressRaw.split(',').map((p) => p.trim()).filter(Boolean);

    let addresseeName = '';
    let address1 = '';
    let address2 = '';
    if (addressParts.length === 1) {
      address1 = addressParts[0];
    } else if (addressParts.length === 2) {
      addresseeName = addressParts[0];
      address1 = addressParts[1];
    } else if (addressParts.length >= 3) {
      addresseeName = addressParts[0];
      address1 = addressParts[1];
      address2 = addressParts.slice(2).join(', ');
    }

    const customerName = addresseeName || companyTo || String(shippingTo?.name || '').trim();
    const addressLine1 = [address1, address2].filter(Boolean).join(', ');
    const city = String(shippingTo?.city || '').trim();
    const state = String(shippingTo?.state || '').trim();
    const zip = String(shippingTo?.zip || '').trim();
    const country = String(shippingTo?.country || 'US').trim() || 'US';
    const cityStateZip = `${city}${city && state ? ' ' : ''}${state}${(city || state) && zip ? ' ' : ''}${zip}`.trim();

    const itemRaw = String(packingSlip?.item || '').trim();
    const isParts = itemRaw.toUpperCase() === 'PARTS';
    const partsDescription = String(
      packingSlip?.description || packingSlip?.Description || packingSlip?.itemDescription || packingSlip?.item_description || ''
    ).trim();
    const itemSku = escapeZpl((isParts ? partsDescription : itemRaw).slice(0, 30));
    const quantity = escapeZpl(String(packingSlip?.quantity || '').trim());

    const packageTextRaw = String(packingSlip?.package_text || '').trim();
    const packageText = escapeZpl(packageTextRaw.replaceAll('\r\n', ' ').replaceAll('\n', ' ').replaceAll('\r', ' '));
    let footerZpl = '^CF0,32\n^FO40,920^FB730,5,6,L^FD^FS';
    if (packageText.length > 0 && packageText.length <= 250) {
      footerZpl = `^CF0,32\n^FO40,920^FB730,5,6,L^FD${packageText}^FS`;
    } else if (packageText.length <= 500) {
      footerZpl = `^CF0,28\n^FO40,920^FB730,7,6,L^FD${packageText}^FS`;
    } else {
      footerZpl = `^CF0,24\n^FO40,920^FB730,10,6,L^FD${packageText}^FS`;
    }

    const orderStartY = 85 + (poLineCount * 35);

    return `^XA
^CI28
^PO0
^FWN
^PW812
^LL1218
^LH0,40

^FX =========================
^FX COMPANY LOGO / NAME (TOP LEFT – BIG TEXT)
^FX =========================
${logoZpl ? `${logoZpl}` : ''}

^FX =========================
^FX TOP RIGHT INFO (RIGHT ALIGNED)
^FX =========================
^CF0,28
^FO430,15^FB350,1,0,R^FD${escapeZpl(soNumber)}^FS
^FO430,50^FB350,1,0,R^FDSHIP DATE: ${escapeZpl(formattedShipDate)}^FS
^FO430,85^FB350,2,5,R^FDPO #: ${escapeZpl(poNumber)}^FS
${internalId ? `^FO430,${orderStartY}^FB350,2,5,R^FDORDER #: ${escapeZpl(internalId)}^FS` : ''}

^FX =========================
^FX SHIP VIA (BIG FONT - RIGHT ALIGNED)
^FX =========================
^CF0,75
${shipViaZpl}

^FX =========================
^FX SHIP TO (LEFT ALIGNED)
^FX =========================
^CF0,34
^FO35,280^FDSHIP TO:^FS
^CF0,30
^FO35,325^FD${escapeZpl(customerName)}^FS
^FO35,362^FD${escapeZpl(addressLine1)}^FS
^FO35,399^FD${escapeZpl(cityStateZip)}^FS
^FO35,436^FD${escapeZpl(country)}^FS

^FX =========================
^FX REF BARCODE (CENTER ALIGNED WITH VALUE UNDER IT)
^FX =========================
^BY3,4,154
^FO165,520^FB912,1,0,C
^BCN,144,Y,N,N^FD${escapeZpl(packingSlipId)}^FS

^FX =========================
^FX ITEM HEADER (CENTER ALIGNED LIKE QUANTITY)
^FX =========================
^FO30,780^GB500,55,3^FS
^FO530,780^GB250,55,3^FS
^CF0,30
^FO40,795^FB480,1,0,C^FDITEM^FS
^FO540,795^FB230,1,0,C^FDQUANTITY^FS

^FX =========================
^FX ITEM ROW (CENTER ALIGNED LIKE QUANTITY)
^FX =========================
^FO30,835^GB500,60,3^FS
^FO530,835^GB250,60,3^FS
^CF0,30
^FO40,855^FB480,1,0,C^FD${itemSku}^FS
^FO540,855^FB230,1,0,C^FD${quantity}^FS

^FX =========================
^FX FOOTER (DYNAMIC SIZE BASED ON TEXT LENGTH)
^FX =========================
${footerZpl}

^XZ`;
  };

  const inferDocumentMimeType = (base64) => {
    const signature = String(base64 || '').trim().slice(0, 20);
    if (signature.startsWith('JVBERi0')) return 'application/pdf';
    if (signature.startsWith('iVBOR')) return 'image/png';
    if (signature.startsWith('/9j/')) return 'image/jpeg';
    if (signature.startsWith('R0lGOD')) return 'image/gif';
    if (signature.startsWith('UklGR')) return 'image/webp';
    return 'application/octet-stream';
  };

  const fileExtensionForMime = (mime) => {
    if (mime === 'application/pdf') return 'pdf';
    if (mime === 'image/png') return 'png';
    if (mime === 'image/jpeg') return 'jpg';
    if (mime === 'image/gif') return 'gif';
    if (mime === 'image/webp') return 'webp';
    return 'bin';
  };

  const createBlobUrlFromBase64 = (base64, mimeType) => {
    try {
      const clean = String(base64 || '').replace(/\s+/g, '');
      const binary = atob(clean);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to decode document base64:', error);
      return null;
    }
  };


  // Map JSON to ZPL helper
  async function mapJsonToZpl(raw, fallbackSsccCode = '') {
    const packingSlip = normalizePackingSlip(raw?.packing_slip ?? raw?.packingSlip ?? raw);
    if (packingSlip) {
      const logoZpl = await buildPackingSlipImageZpl(packingSlip);
      return generatePackingSlipZplFromPackingSlip(packingSlip, logoZpl);
    }

    // labelData2 (primary) or label_data (fallback) — same pattern as SalesOrders
    const data = raw?.labelData2 ? raw.labelData2 : (raw?.label_data ? raw.label_data : raw || {});
    const from = data.shipping_from || data.ship_from || {};
    const to = data.shipping_to || data.to || {};

    const companyFrom = String(from.company || from.name || from.addressee || '');
    const addressFrom = String(from.address || from.address1 || '');
    const cityFrom = String(from.city || '');
    const stateFrom = String(from.state || '');
    const zipFrom = String(from.zip || from.postal || from.postal_code || '');

    const companyTo = String(to.company || '');
    const addresseeTo = String(to.addressee || '');
    const adress1 = String(to.address || to.address1 || '');
    const adress2 = String(to.address2 || '');
    const cityTo = String(to.city || '');
    const stateTo = String(to.state || '');
    const zipTo = String(to.zip || to.postal || to.postal_code || '');

    // For the template placeholders:
    const company = companyTo || addresseeTo || '';
    const adress = companyTo ? addresseeTo : ''; // If both exist, company on top, addressee on second line

    const zipCode = String(data.zip_code || data.zip || zipTo || '');
    const zipDisplay = String(data.zip_display || zipCode);
    const carrierName = String(data.carrier || data.carrier_name || data.ship_via || data.ship_via_name || '').trim();
    const proNumber = String(data.pro_number || data.pro || '');
    const bolNumber = String(data.bol_number || data.bol || '');
    const bolNumberDisplay = String(bolNumber || data.bol_number_display || '');
    const poNumber = String(data.po_number || data.po || '');
    const upc = String(data.upc || data.item || '');
    const qtyDisplay = String(data.quantity ?? data.qty ?? '');
    const cartonInfo = String(data.carton_info || data.carton || '');
    const model = String(data.model || data.description || data.item || '');
    const ssccCode = String(data.sscc_code || data.sscc || raw?.sscc_code || fallbackSsccCode || '');
    const ssccDisplay = String(data.sscc_display || ssccCode);

    const hasAdress2 = adress2.trim().length > 0;
    const addressBlock = hasAdress2
      ? `^FO420,145^FD${adress1}^FS\n^FO420,175^FD${adress2}^FS\n^FO420,205^FD${cityTo}, ${stateTo} ${zipTo}^FS`
      : `^FO420,145^FD${adress1}^FS\n^FO420,175^FD${cityTo}, ${stateTo} ${zipTo}^FS`;

    const isLongCarrier = carrierName.length > 22;
    const carrierZpl = isLongCarrier
      ? `^CF0,28\n^FO470,315^FB280,2,4,L,0^FD${carrierName}^FS`
      : `^CF0,28\n^FO470,315^FD${carrierName}^FS`;
    const proBolFontCmd = isLongCarrier ? '^CF0,22' : '^CF0,24';
    const proY = isLongCarrier ? '385' : '360';
    const bolY = isLongCarrier ? '415' : '390';

    const template = `^XA

^PW772

^LL1178

^CI28

^CF0,38


// --------------------------- HEADER: SHIP FROM / SHIP TO ---------------------------

^FO20,30^FDSHIP FROM:^FS

^FO420,30^FDSHIP TO:^FS


^CF0,28

^FO20,100^FD$companyFrom^FS

^FO20,135^FD$addressFrom^FS

^FO20,170^FD$cityFrom, $stateFrom, $zipFrom^FS


^FO420,85^FD$company^FS

^FO420,115^FD$adress^FS

$addressBlock


^FO390,20^GB2,440,7^FS

^FO15,265^GB740,7,7^FS


// --------------------------- ROUTING BARCODE SECTION ---------------------------

^BY2,2,95

^FO55,315^BCN,95,N,N,N^FD$zipCode^FS

^CF0,26

^FO30,420^FB320,1,0,C,0^FD$zipDisplay^FS


// --------------------------- FREIGHT DETAILS ---------------------------

$carrierZpl

$proBolFontCmd

^FO470,$proY^FDPro#: $proNumber^FS

^FO470,$bolY^FDBOL#: $bolNumberDisplay^FS

^FO15,460^GB740,7,7^FS


// --------------------------- PRODUCT INFORMATION ---------------------------

^CF0,28

^FO20,490^FDPO# $poNumber^FS

^BY2,2,85

^FO420,510^BCN,85,N,N,N^FD$poNumber^FS

^CF0,28

^FO20,635^FDUPC: $upc / SINGLE ASIN^FS

^FO20,675^FDQTY: $qtyDisplay^FS

^CF0,28

^FO20,715^FDCARTON:^FS

^FO140,715^FD$cartonInfo^FS

^CF0,28

^FO20,755^FDMODEL: $model^FS

^FO15,795^GB740,7,7^FS


// --------------------------- SSCC SECTION ---------------------------

^CF0,30

^FO8,825^FB772,1,0,C,0^FDSSCC - 18^FS   // Centered SSCC title


^BY2.5,3,200

^FO2.5,885^BCN,200,N,N,N^FD$ssccCode^FS   // Centered barcode


^CF0,38

^FO0,1120^FB772,1,0,C,0^FD$ssccDisplay^FS  // Centered SSCC display text


^XZ`;

    const filled = template
      .replaceAll('$companyFrom', companyFrom)
      .replaceAll('$addressFrom', addressFrom)
      .replaceAll('$cityFrom', cityFrom)
      .replaceAll('$stateFrom', stateFrom)
      .replaceAll('$zipFrom', zipFrom)
      .replaceAll('$company', company)
      .replaceAll('$adress', adress)
      .replaceAll('$addressBlock', addressBlock)
      .replaceAll('$zipCode', zipCode)
      .replaceAll('$zipDisplay', zipDisplay)
      .replaceAll('$carrierZpl', carrierZpl)
      .replaceAll('$proBolFontCmd', proBolFontCmd)
      .replaceAll('$proY', String(proY))
      .replaceAll('$bolY', String(bolY))
      .replaceAll('$proNumber', proNumber)
      .replaceAll('$bolNumberDisplay', bolNumberDisplay)
      .replaceAll('$poNumber', poNumber)
      .replaceAll('$upc', upc)
      .replaceAll('$qtyDisplay', qtyDisplay)
      .replaceAll('$cartonInfo', cartonInfo)
      .replaceAll('$model', model)
      .replaceAll('$ssccCode', ssccCode)
      .replaceAll('$ssccDisplay', ssccDisplay);

    return filled;
  }

  const formatPngDataUri = (base64OrDataUri) => {
    if (!base64OrDataUri || typeof base64OrDataUri !== 'string') return null;
    const trimmed = base64OrDataUri.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('data:image/')) return trimmed;
    return `data:image/png;base64,${trimmed.replace(/\s+/g, '')}`;
  };

  async function buildLabelPreviewItem(trackingNumber) {
    const response = await api.get('/admin-dropship-reprint-label', { params: { tracking_number: trackingNumber } });
    const payload = response.data || {};
    const itemWarnings = [];

    const documents = (Array.isArray(payload.documents) ? payload.documents : [])
      .map((doc, index) => {
        const mediaBase64 = String(doc?.media || '').trim();
        if (!mediaBase64) return null;
        const mimeType = inferDocumentMimeType(mediaBase64);
        const url = createBlobUrlFromBase64(mediaBase64, mimeType);
        if (!url) return null;
        return {
          url,
          mimeType,
          name: `document_${index + 1}.${fileExtensionForMime(mimeType)}`
        };
      })
      .filter(Boolean);

    // Support labelData array (single or multiple labels) or root-level payload
    const labelDataEntries = Array.isArray(payload.labelData) && payload.labelData.length > 0
      ? payload.labelData
      : [payload];

    const previewItems = [];

    for (let idx = 0; idx < labelDataEntries.length; idx++) {
      const entry = labelDataEntries[idx];
      const entryTracking = String(entry?.tracking_number || entry?.trackingNumber || trackingNumber || '').trim();
      const entryWarnings = [];

      let labelImageUrl = null;
      let isOriginalCarrierZpl = false;

      // 1. Read png_label (Base64-encoded PNG image)
      const rawPngLabel = entry?.png_label || entry?.pngLabel || (idx === 0 ? payload.png_label : null);
      if (rawPngLabel) {
        labelImageUrl = formatPngDataUri(rawPngLabel);
        isOriginalCarrierZpl = false;
      }

      // 2. Fall back to zpl_label if png_label is not provided
      if (!labelImageUrl) {
        const zplBase64 = entry?.zpl_label || (idx === 0 ? payload.zpl_label : null);
        if (zplBase64) {
          try {
            const zpl = atob(zplBase64);
            const labelaryResp = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
              method: 'POST',
              headers: { 'Accept': 'image/png', 'Content-Type': 'application/x-www-form-urlencoded' },
              body: zpl
            });

            if (labelaryResp.ok) {
              const blob = await labelaryResp.blob();
              labelImageUrl = URL.createObjectURL(blob);
              isOriginalCarrierZpl = true;
            } else {
              entryWarnings.push('Failed to convert original label to image');
            }
          } catch (error) {
            console.error('Error converting original dropship ZPL:', error);
            entryWarnings.push(error?.message || 'Failed to convert original label');
          }
        }
      }

      // 3. Fallback to mapped JSON ZPL
      let customLabelImageUrl = null;
      let customLabelSource = null;
      try {
        const zplTemplate = await mapJsonToZpl(payload, '');
        if (zplTemplate) {
          const mappedResp = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
            method: 'POST',
            headers: { 'Accept': 'image/png', 'Content-Type': 'application/x-www-form-urlencoded' },
            body: zplTemplate
          });

          if (mappedResp.ok) {
            const mappedBlob = await mappedResp.blob();
            customLabelImageUrl = URL.createObjectURL(mappedBlob);
            customLabelSource = payload?.labelData2 ? 'labelData2' : (payload?.packing_slip ? 'packing_slip' : 'mapped');
          } else {
            entryWarnings.push('Failed to convert mapped label to image');
          }
        }
      } catch (error) {
        console.error('Error creating mapped dropship label:', error);
        entryWarnings.push(error?.message || 'Failed to create mapped label');
      }

      previewItems.push({
        trackingNumber: entryTracking,
        labelImageUrl,
        customLabelImageUrl,
        customLabelSource,
        rotateLabel: isOriginalCarrierZpl,
        documents: idx === 0 ? documents : [],
        warnings: [...itemWarnings, ...entryWarnings]
      });
    }

    return previewItems.length === 1 ? previewItems[0] : previewItems;
  }

  // Process the data - filter based on status filter
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    // If 'All' is selected, return all data without filtering
    if (statusFilter === 'all') {
      return data;
    }

    const normalizedStatus = statusFilter === 'closed' ? 'closed' : 'open';

    return data.filter(item => {
      const pickedValue = item.picked || item.is_picked || item.ispicked;
      const isComplete = isPickedComplete(pickedValue);

      if (normalizedStatus === 'closed') {
        return isComplete;
      }

      return !isComplete;
    });
  }, [data, statusFilter]);

  useEffect(() => {
    setSelectedLineLevelRows(new Set());
  }, [lineLevelData]);

  useEffect(() => {
    setSelectedDetailedRows(new Set());
  }, [detailedData]);

  useEffect(() => {
    setSelectedViewDetailsRows(new Set());
  }, [viewDetailsData]);

  const openLineLevelDialogForTransaction = async (transactionId, mode = 'nested') => {
    if (!transactionId) return;
    setSelectedTransactionId(transactionId);

    setDetailedDialogOpen(true);
    setDetailedLoading(true);
    setLineLevelDialogOpen(false);
    setLineLevelLoading(false);
    setLineLevelError(null);
    setLineLevelData([]);
    setDetailedData([]);
    setSelectedDetailedRows(new Set());
    setSelectedLineLevelRows(new Set());

    try {
      const response = await api.get(endpoints.dropshipSoLineLevel, {
        params: { transaction_id: transactionId }
      });
      const responseData = response.data?.data ?? response.data ?? [];
      const dataArray = Array.isArray(responseData) ? responseData : (responseData ? [responseData] : []);

      setLineLevelData(dataArray);

      const nestedRows = dataArray.flatMap((item) => (Array.isArray(item?.data) && item.data.length > 0 ? item.data : [item]));
      const detailedItems = nestedRows.length > 0 ? nestedRows : dataArray;

      setDetailedData(detailedItems);
    } catch (err) {
      console.error('Error fetching line-level data:', err);
      setLineLevelError(err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to load line-level data');
      setDetailedData([]);
    } finally {
      setDetailedLoading(false);
    }
  };

  const openLineLevelDialogForRow = (row, mode = 'nested') => {
    if (!row) return;
    const transactionId = row?.sonumber;
    if (!transactionId) {
      console.error('No Sonumber found in row:', row);
      return;
    }
    void openLineLevelDialogForTransaction(transactionId, mode);
  };

  // Generate columns dynamically from the data
  const columns = useMemo(() => {
    if (!processedData || processedData.length === 0) {
      return [];
    }

    // Get all unique keys from the data
    const allKeysSet = new Set();
    processedData.forEach(item => {
      Object.keys(item || {}).forEach(k => allKeysSet.add(k));
    });
    const keys = Array.from(allKeysSet);

    // Define preferred column order
    const primaryOrder = [
      'name',
      'customer_name',
      'status',
      'location',
      'ponumber',
      'po_number',
      'item',
      'quantity',
      'internal_id',
      'transaction_id',
      'reference_1',
      'ship_from',
      'shipping_address',
      'scheduled_pickup_date',
      'date_created',
      'created_at',
      'is_picked',
      'ispicked',
      'scac',
      'carrier_name'
    ];

    // Separate primary and other fields
    const primaries = primaryOrder.filter(k => keys.includes(k));
    const rest = keys.filter(k => !primaryOrder.includes(k) && !['created_at', 'updated_at'].includes(k));
    const ordered = [...primaries, ...rest];

    // Generate columns
    const generatedColumns = ordered.map(key => {
      const shouldFormatPicked = key.includes('picked') || key === 'ready_for_picking' || key === 'ready_for_pick' || key === 'ship_confirmed';
      return {
        accessorKey: key,
        header: key
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          const value = info.getValue();
          const normalizedValue = shouldFormatPicked ? formatPickedCount(value) : value;
          if (typeof normalizedValue === 'number') {
            return Math.round(normalizedValue);
          }
          if (typeof normalizedValue === 'object' && normalizedValue !== null) {
            return JSON.stringify(normalizedValue);
          }
          return normalizedValue || '-';
        }
      };
    });

    const viewButtonColumn = {
      accessorKey: 'view_details',
      header: 'View Details',
      forceFirst: true,
      minSize: 80,
      size: 100,
      cell: info => {
        const row = info.row?.original || info.getValue();
        return (
          <Button
            size="small"
            variant="contained"
            onClick={(e) => {
              if (e && e.stopPropagation) e.stopPropagation();
              openLineLevelDialogForRow(row, 'nested');
            }}
          >
            View Details
          </Button>
        );
      }
    };

    return [viewButtonColumn, ...generatedColumns];
  }, [processedData]);

  // Helper function to get unique row ID
  const getRowId = (row, index) => {
    const id = row.id || row.internal_id || '';
    const item = String(row.item || '');
    const quantity = String(row.quantity || '');
    const scac = String(row.scac || '');
    const scheduledPickup = String(row.scheduled_pickup_date || '');
    const transactionId = String(row.transaction_id || '');
    const name = String(row.name || '');

    const uniqueKey = [
      id,
      item,
      quantity,
      scac,
      scheduledPickup,
      transactionId,
      name,
      index !== undefined ? index : ''
    ].filter(Boolean).join('|');

    return `row_${uniqueKey}`;
  };

  // Transform values for export (CSV/Excel)
  const exportValueTransform = (val, row, columnId) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE';

    let processedVal = val;
    const shouldFormat = columnId && (columnId.includes('picked') || columnId === 'ready_for_picking' || columnId === 'ready_for_pick' || columnId === 'ship_confirmed');
    if (shouldFormat) {
      processedVal = formatPickedCount(val);
    }

    if (Array.isArray(processedVal)) return processedVal.map(v => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join('; ');
    if (typeof processedVal === 'object') return JSON.stringify(processedVal);
    return String(processedVal);
  };

  // Handle row click to fetch line-level data in dialog
  const handleRowClick = useCallback((row) => {
    openLineLevelDialogForRow(row, 'nested');
  }, []);

  // Handle row click in line-level table to show detailed data
  const handleLineLevelRowClick = useCallback((row) => {
    if (!row || !row.data || !Array.isArray(row.data)) {
      return;
    }

    setDetailedLoading(true);
    setDetailedDialogOpen(true);
    setDetailedData(row.data);
    setDetailedLoading(false);
  }, []);

  // Handle View Details button click - fetch line-level data based on status and ship date filters
  const handleViewDetails = useCallback(async () => {
    setViewDetailsDialogOpen(true);
    setViewDetailsLoading(true);
    setViewDetailsError(null);
    setViewDetailsData([]);

    try {
      const normalizedStatus = statusFilter === 'closed' ? 'closed' : 'open';
      const params = { status: normalizedStatus };
      if (shipDateFrom) params.order_date_from = shipDateFrom;
      if (shipDateTo) params.order_date_to = shipDateTo;
      if (trackingNumber) params.tracking_number = trackingNumber;
      if (transactionId) params.transaction_id = transactionId;
      if (poNumber) params.po_number = poNumber;

      const response = await api.get(endpoints.dropshipSalesOrders, { params });
      const responseData = response.data?.data ?? response.data ?? [];
      const dataArray = Array.isArray(responseData) ? responseData : (responseData ? [responseData] : []);
      setViewDetailsData(dataArray);
    } catch (err) {
      console.error('Error fetching view details data:', err);
      setViewDetailsError(err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to load line-level data');
      setViewDetailsData([]);
    } finally {
      setViewDetailsLoading(false);
    }
  }, [statusFilter, shipDateFrom, shipDateTo, trackingNumber, transactionId, poNumber]);

  // Apply filter drafts (dates and tracking number) to active filters and refetch
  const handleApplyFilters = useCallback((newShipDateFrom, newShipDateTo, newTrackingNumber, newTransactionId, newPoNumber) => {
    setShipDateFrom(newShipDateFrom);
    setShipDateTo(newShipDateTo);
    setTrackingNumber(newTrackingNumber);
    setTransactionId(newTransactionId);
    setPoNumber(newPoNumber);

    localStorage.setItem('dropship-tracking-number', newTrackingNumber || '');
    localStorage.setItem('dropship-transaction-id', newTransactionId || '');
    localStorage.setItem('dropship-po-number', newPoNumber || '');
  }, []);

  // State for label preview dialog
  const [labelPreviewItems, setLabelPreviewItems] = useState([]);
  const [labelLoading, setLabelLoading] = useState(false);
  const [labelError, setLabelError] = useState(null);
  const [labelPreviewOpen, setLabelPreviewOpen] = useState(false);

  const clearLabelPreviewItems = useCallback(() => {
    setLabelPreviewItems((prev) => {
      prev.forEach((item) => {
        if (item?.labelImageUrl && item.labelImageUrl.startsWith('blob:')) URL.revokeObjectURL(item.labelImageUrl);
        if (item?.customLabelImageUrl && item.customLabelImageUrl.startsWith('blob:')) URL.revokeObjectURL(item.customLabelImageUrl);
        (item?.documents || []).forEach((doc) => {
          if (doc?.url && doc.url.startsWith('blob:')) URL.revokeObjectURL(doc.url);
        });
      });
      return [];
    });
  }, []);

  const closeLabelPreviewDialog = useCallback(() => {
    setLabelPreviewOpen(false);
    clearLabelPreviewItems();
    setLabelError(null);
  }, [clearLabelPreviewItems]);

  const handleGenerateLabel = useCallback(async (trackingNumber) => {
    if (!trackingNumber) return;

    setLabelLoading(true);
    setLabelError(null);
    clearLabelPreviewItems();
    setLabelPreviewOpen(true);

    try {
      const previewItem = await buildLabelPreviewItem(trackingNumber);
      const items = Array.isArray(previewItem) ? previewItem : [previewItem];
      setLabelPreviewItems(items);
      const hasAny = items.some(item => item.labelImageUrl || item.customLabelImageUrl || (item.documents && item.documents.length > 0));
      if (!hasAny) {
        setLabelError('No label preview available.');
      } else {
        const allWarnings = items.flatMap(item => item.warnings || []);
        if (allWarnings.length) {
          setLabelError(allWarnings.join('\n'));
        }
      }
    } catch (err) {
      console.error('Error fetching dropship label data:', err);
      setLabelError(err?.response?.data?.message || err?.message || 'Failed to fetch label data');
    } finally {
      setLabelLoading(false);
    }
  }, [clearLabelPreviewItems]);

  const handleGenerateMultipleLabels = useCallback(async (trackingNumbers) => {
    const uniqueTrackingNumbers = Array.from(new Set((trackingNumbers || []).filter(Boolean)));
    if (uniqueTrackingNumbers.length === 0) {
      setLabelError('Please select at least one row with a tracking number.');
      return;
    }

    setLabelLoading(true);
    setLabelError(null);
    clearLabelPreviewItems();
    setLabelPreviewOpen(true);

    const previewItems = [];
    const warnings = [];

    try {
      for (const trackingNumber of uniqueTrackingNumbers) {
        try {
          const previewItem = await buildLabelPreviewItem(trackingNumber);
          const items = Array.isArray(previewItem) ? previewItem : [previewItem];
          items.forEach((item) => {
            if (
              item.labelImageUrl ||
              item.customLabelImageUrl ||
              (item.documents && item.documents.length > 0)
            ) {
              previewItems.push(item);
            } else {
              warnings.push(`${item.trackingNumber || trackingNumber}: No label preview available.`);
            }

            if (item.warnings?.length) {
              warnings.push(...item.warnings.map((message) => `${item.trackingNumber || trackingNumber}: ${message}`));
            }
          });
        } catch (error) {
          console.error(`Error fetching dropship label for ${trackingNumber}:`, error);
          warnings.push(`${trackingNumber}: ${error?.response?.data?.message || error?.message || 'Failed to fetch label data'}`);
        }
      }

      setLabelPreviewItems(previewItems);

      if (warnings.length > 0) {
        setLabelError(warnings.join('\n'));
      } else if (previewItems.length === 0) {
        setLabelError('No labels could be generated.');
      }
    } finally {
      setLabelLoading(false);
    }
  }, [clearLabelPreviewItems]);

  const handleGenerateSelectedLabels = useCallback((rows, selectedRows) => {
    const trackingNumbers = getSelectedTrackingNumbers(rows, selectedRows);
    return handleGenerateMultipleLabels(trackingNumbers);
  }, [getSelectedTrackingNumbers, handleGenerateMultipleLabels]);

  const lineLevelSelectableRowKeys = useMemo(() => getSelectableRowKeys(lineLevelData), [lineLevelData, getSelectableRowKeys]);
  const detailedSelectableRowKeys = useMemo(() => getSelectableRowKeys(detailedData), [detailedData, getSelectableRowKeys]);
  const viewDetailsSelectableRowKeys = useMemo(() => getSelectableRowKeys(viewDetailsData), [viewDetailsData, getSelectableRowKeys]);

  const createSelectionColumn = useCallback((selectedRows, setSelectedRows) => ({
    accessorKey: '__select_labels',
    header: '\u200B',
    minSize: 60,
    size: 70,
    forceFirst: true,
    cell: (info) => {
      const row = info.row?.original || info.getValue();
      const trackingNumber = getTrackingNumber(row);
      if (!trackingNumber) return '-';

      const rowKey = getRowSelectionKey(row);
      return (
        <Box
          data-checkbox-cell="true"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
        >
          <Checkbox
            checked={selectedRows.has(rowKey)}
            onChange={(e) => {
              e.stopPropagation();
              updateSelection(setSelectedRows, rowKey, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
        </Box>
      );
    }
  }), [getRowSelectionKey, getTrackingNumber, updateSelection]);

  // Generate columns for line-level table
  const buildLineLevelColumns = useCallback((selectedRows, setSelectedRows) => [
    createSelectionColumn(selectedRows, setSelectedRows),
    {
      accessorKey: 'generate_label',
      header: 'Generate Label',
      minSize: 120,
      size: 140,
      cell: info => {
        const row = info.row?.original || info.getValue();
        const trackingNumber = getTrackingNumber(row);
        return trackingNumber ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleGenerateLabel(trackingNumber)}
            disabled={labelLoading}
          >
            Generate Label
          </Button>
        ) : '-';
      }
    },
    {
      accessorKey: 'line_id',
      header: 'Line ID',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'po_number',
      header: 'PO Number',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'item',
      header: 'Item',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'quantity',
      header: 'Quantity',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        return value !== null && value !== undefined ? value : '-';
      }
    },
    {
      accessorKey: 'location',
      header: 'Location',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => info.getValue() || '-'
    },
    {
      accessorKey: 'ready_for_picking',
      header: 'Ready For Picking',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        const normalizedValue = formatPickedCount(value);
        return normalizedValue || '-';
      }
    },
    {
      accessorKey: 'picked',
      header: 'Picked',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        const normalizedValue = formatPickedCount(value);
        return normalizedValue || '-';
      }
    },
    {
      accessorKey: 'ship_confirmed',
      header: 'Ship Confirmed',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        const normalizedValue = formatPickedCount(value);
        return normalizedValue || '-';
      }
    }
  ], [createSelectionColumn, getTrackingNumber, handleGenerateLabel, labelLoading]);

  const reasonColumn = useMemo(() => ({
    accessorKey: 'reason',
    header: 'Reason',
    minSize: COLUMN_MIN_SIZE,
    size: COLUMN_SIZE,
    cell: info => info.getValue() || '-'
  }), []);

  const lineLevelColumns = useMemo(() => {
    const cols = buildLineLevelColumns(selectedLineLevelRows, setSelectedLineLevelRows);
    const hasReason = lineLevelData.some(row => row?.reason != null && row?.reason !== '');
    if (hasReason) {
      cols.push(reasonColumn);
    }
    return cols;
  }, [buildLineLevelColumns, selectedLineLevelRows, lineLevelData, reasonColumn]);

  const viewDetailsColumns = useMemo(() => {
    const fullColumns = buildLineLevelColumns(selectedViewDetailsRows, setSelectedViewDetailsRows);
    const filtered = fullColumns.filter((column) => column.accessorKey !== '__select_labels' && column.accessorKey !== 'generate_label');
    const hasReason = viewDetailsData.some(row => row?.reason != null && row?.reason !== '');
    if (hasReason) {
      filtered.push(reasonColumn);
    }
    return filtered;
  }, [buildLineLevelColumns, selectedViewDetailsRows, viewDetailsData, reasonColumn]);
  // Generate columns for detailed data table
  const detailedColumns = useMemo(() => {
    if (!detailedData || detailedData.length === 0) {
      return [];
    }

    const keys = Object.keys(detailedData[0] || {});
    const orderedKeys = [
      'line_id',
      'item',
      'reason',
      'tracking_number',
      'delivered',
      'delivery_status',
      'delivery_time',
      'sscc_codes',
      'packing_slip',
      'bin_number',
      'picked_time',
      'picker_name',
      'ship_confirm_time',
      'movement'
    ];

    // Order keys according to preferred order, then add any remaining keys
    const ordered = [
      ...orderedKeys.filter(k => keys.includes(k)),
      ...keys.filter(k => !orderedKeys.includes(k))
    ];

    // Add selection and Generate Label columns first
    const selectionColumn = createSelectionColumn(selectedDetailedRows, setSelectedDetailedRows);
    const generateLabelColumn = {
      accessorKey: 'generate_label',
      header: 'Generate Label',
      minSize: 120,
      size: 140,
      cell: info => {
        const row = info.row?.original || info.getValue();
        const trackingNumber = getTrackingNumber(row);
        return trackingNumber ? (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleGenerateLabel(trackingNumber);
            }}
            disabled={labelLoading}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Generate Label
          </Button>
        ) : '-';
      }
    };

    const otherColumns = ordered.map(key => {
      const shouldFormatPicked = key.includes('picked') || key === 'ready_for_picking' || key === 'ready_for_pick' || key === 'ship_confirmed';
      return {
        accessorKey: key,
        header: key
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        minSize: COLUMN_MIN_SIZE,
        size: COLUMN_SIZE,
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined || value === '') {
            return '-';
          }
          const displayValue = shouldFormatPicked ? formatPickedCount(value) : value;
          return String(displayValue);
        }
      };
    });
    return [selectionColumn, generateLabelColumn, ...otherColumns];
  }, [createSelectionColumn, detailedData, getTrackingNumber, handleGenerateLabel, labelLoading, selectedDetailedRows]);

  const dropshipSalesOrdersMainGrid = useDataGridProTable({
    data: processedData,
    loading: isLoading,
    error: error || null,
    title: 'Regular Pick Sales Orders',
    hideQuickFilter: true,
    columns,
    onDataRefresh: handleRefetch,
    getRowId: (row, index) => getRowId(row, index),
    exportValueTransform,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: handleRowClick,
    muiTableContainerProps: { sx: {} },
    actionButtons: [
      <DropshipFilterBar
        key="dropship-filter-bar"
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        shipDateFrom={shipDateFrom}
        shipDateTo={shipDateTo}
        trackingNumber={trackingNumber}
        transactionId={transactionId}
        poNumber={poNumber}
        onApply={handleApplyFilters}
        onViewDetails={handleViewDetails}
      />,
    ],
  });

  const dropshipLineLevelGrid = useDataGridProTable({
    data: lineLevelData || [],
    columns: lineLevelColumns,
    loading: false,
    title: 'dropship-line-level',
    exportValueTransform,
    initialColumnVisibility: lineLevelColumnVisibility,
    onColumnVisibilityChange: setLineLevelColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: lineLevelAllowNested ? handleLineLevelRowClick : undefined,
    muiTableContainerProps: { sx: {} },
    disablePaper: true,
    error: null,
  });

  const handleDetailedRowClick = useCallback((row, event) => {
    if (event?.target?.closest('button') || event?.target?.closest('a') || event?.target?.closest('input')) {
      return;
    }
    if (rowHasLabelIdentifier(row)) {
      const rowKey = getRowSelectionKey(row);
      setSelectedDetailedRows((prev) => {
        const next = new Set(prev);
        if (next.has(rowKey)) {
          next.delete(rowKey);
        } else {
          next.add(rowKey);
        }
        return next;
      });
    }
  }, [getRowSelectionKey, rowHasLabelIdentifier]);

  const dropshipDetailedGrid = useDataGridProTable({
    data: detailedData || [],
    columns: detailedColumns,
    loading: false,
    title: 'dropship-detailed',
    exportValueTransform,
    initialColumnVisibility: detailedColumnVisibility,
    onColumnVisibilityChange: setDetailedColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: handleDetailedRowClick,
    muiTableContainerProps: { sx: {} },
    disablePaper: true,
    error: null,
  });

  const dropshipViewDetailsGrid = useDataGridProTable({
    data: viewDetailsData || [],
    columns: viewDetailsColumns,
    loading: false,
    title: 'dropship-view-details',
    exportValueTransform,
    initialColumnVisibility: viewDetailsColumnVisibility,
    onColumnVisibilityChange: setViewDetailsColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: handleLineLevelRowClick,
    muiTableContainerProps: { sx: {} },
    disablePaper: true,
    error: null,
  });

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <MuiXDataGridSurface hook={dropshipSalesOrdersMainGrid} DataGridPro={DataGridPro} />
      </Box>

      {/* First Level Nested Table Dialog - Line Level Data */}
      <Dialog
        open={lineLevelDialogOpen}
        onClose={() => { setLineLevelDialogOpen(false); setLineLevelAllowNested(true); }}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{
          sx: {
            width: '96vw',
            maxWidth: '96vw',
            height: '92vh',
            maxHeight: '92vh',
          }
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>
            Line Level Data - {selectedTransactionId || 'N/A'}
          </Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => { setLineLevelDialogOpen(false); setLineLevelAllowNested(true); }}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {lineLevelLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : lineLevelError ? (
            <Typography color="error" sx={{ p: 2 }}>
              {lineLevelError}
            </Typography>
          ) : lineLevelData && lineLevelData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 2, pb: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={lineLevelSelectableRowKeys.length > 0 && selectedLineLevelRows.size === lineLevelSelectableRowKeys.length}
                    indeterminate={selectedLineLevelRows.size > 0 && selectedLineLevelRows.size < lineLevelSelectableRowKeys.length}
                    onChange={(e) => toggleSelectAllRows(setSelectedLineLevelRows, lineLevelData, e.target.checked)}
                    disabled={lineLevelSelectableRowKeys.length === 0}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {selectedLineLevelRows.size > 0 ? `${selectedLineLevelRows.size} selected` : 'Select rows to generate labels'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={labelLoading ? <CircularProgress size={16} /> : <PrintIcon />}
                  onClick={() => handleGenerateSelectedLabels(lineLevelData, selectedLineLevelRows)}
                  disabled={labelLoading || selectedLineLevelRows.size === 0}
                >
                  Generate Labels ({selectedLineLevelRows.size})
                </Button>
              </Box>
              <MuiXDataGridSurface hook={dropshipLineLevelGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography sx={{ p: 2 }}>No line-level data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Second Level Nested Table Dialog - Detailed Data */}
      <Dialog
        open={detailedDialogOpen}
        onClose={() => setDetailedDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{
          sx: {
            width: '96vw',
            maxWidth: '96vw',
            height: '92vh',
            maxHeight: '92vh',
          }
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>Detailed Data</Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => setDetailedDialogOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {detailedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : detailedData && detailedData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 2, pb: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={detailedSelectableRowKeys.length > 0 && selectedDetailedRows.size === detailedSelectableRowKeys.length}
                    indeterminate={selectedDetailedRows.size > 0 && selectedDetailedRows.size < detailedSelectableRowKeys.length}
                    onChange={(e) => toggleSelectAllRows(setSelectedDetailedRows, detailedData, e.target.checked)}
                    disabled={detailedSelectableRowKeys.length === 0}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {selectedDetailedRows.size > 0 ? `${selectedDetailedRows.size} selected` : 'Select rows to generate labels'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={labelLoading ? <CircularProgress size={16} /> : <PrintIcon />}
                  onClick={() => handleGenerateSelectedLabels(detailedData, selectedDetailedRows)}
                  disabled={labelLoading || selectedDetailedRows.size === 0}
                >
                  Generate Labels ({selectedDetailedRows.size})
                </Button>
              </Box>
              <MuiXDataGridSurface hook={dropshipDetailedGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography sx={{ p: 2 }}>No detailed data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* View Details Dialog - All Open Orders Line-Level Data */}
      <Dialog
        open={viewDetailsDialogOpen}
        onClose={() => setViewDetailsDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{
          sx: {
            width: '96vw',
            maxWidth: '96vw',
            height: '92vh',
            maxHeight: '92vh',
          }
        }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>
            View Details - {statusFilter === 'closed' ? 'Closed Orders' : 'Open Orders'} Summary
          </Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => setViewDetailsDialogOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {viewDetailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : viewDetailsError ? (
            <Typography color="error" sx={{ p: 2 }}>
              {viewDetailsError}
            </Typography>
          ) : viewDetailsData && viewDetailsData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={dropshipViewDetailsGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography sx={{ p: 2 }}>No line-level data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={labelPreviewOpen}
        onClose={closeLabelPreviewDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { width: '90%', maxWidth: '1200px', maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.1rem', fontWeight: 500, lineHeight: 1.6 }}>
            Shipping Label Preview{labelPreviewItems.length > 1 ? ` (${labelPreviewItems.length})` : ''}
          </Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={closeLabelPreviewDialog}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, p: 2 }}>
          {labelLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 1.5 }}>Generating labels...</Typography>
            </Box>
          ) : (
            <>
              {labelError && (
                <Typography color="error" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                  {labelError}
                </Typography>
              )}
              {labelPreviewItems.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, overflowY: 'auto' }}>
                  {labelPreviewItems.map((item, index) => (
                    <Box key={`${item.trackingNumber}_${index}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                      <Typography sx={{ fontWeight: 600, mb: 2 }}>
                        Tracking Number: {item.trackingNumber || 'N/A'}
                      </Typography>
                      {item.labelImageUrl && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                          <Typography sx={{ fontWeight: 500, mb: 1 }}>Shipping Label</Typography>
                          <img
                            src={item.labelImageUrl}
                            alt="Shipping Label"
                            style={{
                              maxWidth: '100%',
                              width: 'auto',
                              maxHeight: 520,
                              objectFit: 'contain',
                              border: '1px solid #ccc',
                              marginBottom: 8,
                              ...(item.rotateLabel === false ? {} : { transform: 'rotate(180deg)', transformOrigin: 'center center' })
                            }}
                          />
                          <Button variant="contained" href={item.labelImageUrl} download={`shipping_label_${item.trackingNumber || index}.png`} sx={{ mt: 1 }}>
                            Download Label
                          </Button>
                        </Box>
                      )}
                      {item.customLabelImageUrl && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                          <Typography sx={{ fontWeight: 500, mb: 1 }}>{item.customLabelSource || 'Custom Label'}</Typography>
                          <img
                            src={item.customLabelImageUrl}
                            alt="Custom Shipping Label"
                            style={{ maxWidth: 400, border: '1px solid #ccc', marginBottom: 8 }}
                          />
                          <Button variant="contained" href={item.customLabelImageUrl} download={`${(item.customLabelSource || 'custom').replace(/\s+/g, '_')}_${item.trackingNumber || index}.png`}>
                            Download Label
                          </Button>
                        </Box>
                      )}
                      {item.documents?.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, width: '100%' }}>
                          <Typography sx={{ fontWeight: 500, mb: 1 }}>Documents</Typography>
                          {item.documents.map((doc, docIndex) => (
                            <Box key={`${item.trackingNumber}_${doc.name}_${docIndex}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mb: 2 }}>
                              {doc.mimeType.startsWith('image/') ? (
                                <img src={doc.url} alt={`Document ${docIndex + 1}`} style={{ maxWidth: 400, border: '1px solid #ccc', marginBottom: 8 }} />
                              ) : doc.mimeType === 'application/pdf' ? (
                                <iframe src={doc.url} title={`Document ${docIndex + 1}`} style={{ width: '100%', maxWidth: 460, height: 420, border: '1px solid #ccc', marginBottom: 8 }} />
                              ) : (
                                <Typography sx={{ mb: 1 }}>Preview not available for this document type.</Typography>
                              )}
                              <Button variant="contained" href={doc.url} download={doc.name}>
                                Download Document {docIndex + 1}
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : !labelError ? (
                <Typography sx={{ p: 2 }}>No label preview available.</Typography>
              ) : null}
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default DropshipSalesOrders;
